/**
 * Reserva de recursos/áreas comuns — Portal do Condômino (Fase 2, 0.1.32).
 * Morador vê os recursos e solicita reserva; síndico cadastra recursos e aprova/recusa.
 *
 * 0.1.89 (Doc. Técnica §68): cobrança da taxa. No recurso, o síndico liga "Cobrar no
 * próximo boleto (pela administradora)"; o morador só reserva aceitando a cobrança (texto e
 * data guardados). Sem a opção, o pagamento é combinado direto ("Como pagar"). A gestão vê
 * as cobranças pendentes, baixa o PDF, copia/abre o e-mail para a administradora e marca
 * como enviadas; reserva cancelada depois de enviada vira "pedir cancelamento da cobrança".
 * A reserva passou a guardar a UNIDADE (do cadastro; quem tem dois aptos escolhe).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Briefcase, CalendarClock, Check, Download, Loader2, Pencil, Plus, Receipt, Trash2, X,
} from "lucide-react";
import { api, ApiError, reservasCobrancasPdfUrl } from "../lib/api";
import { AVISO_ANEXO, BotoesEmail } from "./EmailPronto";

interface Recurso {
  id: number; nome: string; descricao: string | null; precisa_aprovacao: boolean; taxa: number | null;
  regras: string | null; cobranca_boleto: boolean; pagamento_instrucoes: string | null;
}
interface RecursosResp { condominio_id: number; itens: Recurso[]; pode_gerir: boolean; minhas_unidades: string[] }
interface Reserva {
  id: number; recurso_id: number; recurso_nome: string | null; unidade: string | null; usuario_nome: string | null;
  inicio: string | null; fim: string | null; status: string; observacao: string | null; meu: boolean;
  valor: number | null; cobranca_boleto: boolean; aceite_cobranca_em: string | null; aceite_texto: string | null;
  informada_adm_em: string | null;
}
interface ReservasResp { condominio_id: number; itens: Reserva[]; pode_gerir: boolean }
interface ItemCobranca {
  id: number; unidade: string | null; usuario_nome: string | null; recurso_nome: string | null;
  inicio: string | null; valor: number; status: string; aceite_cobranca_em: string | null;
  informada_adm_em: string | null; estorno_adm_em: string | null;
}
interface Cobrancas {
  pendentes: ItemCobranca[]; aguardando_aprovacao: ItemCobranca[]; informadas: ItemCobranca[];
  estornar: ItemCobranca[]; estornos_pedidos: ItemCobranca[];
}

export const brl = (v: number | null) => v == null ? "" : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDT = (iso: string | null) => iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
const CORES: Record<string, string> = {
  solicitada: "bg-amber-100 text-amber-800", aprovada: "bg-emerald-100 text-emerald-800",
  recusada: "bg-rose-100 text-rose-700", cancelada: "bg-slate-100 text-slate-500",
};
const msgErro = (e: unknown, padrao: string) => (e instanceof ApiError ? e.message : padrao);

/** Texto que o morador aceita (o servidor grava o texto definitivo, com a unidade e a hora). */
export function textoAceite(r: Recurso, data: string, hIni: string, unidade: string | null): string {
  const [a, m, d] = (data || "aaaa-mm-dd").split("-");
  return `Autorizo a cobrança de ${brl(r.taxa)}, referente à reserva de ${r.nome} em ${d}/${m}/${a} ${hIni}, `
    + `no próximo boleto do condomínio${unidade ? ` da unidade ${unidade}` : ""}. `
    + "Se a reserva for recusada ou cancelada, a cobrança não é feita.";
}

type FormRec = { nome: string; desc: string; aprov: boolean; taxa: string; boleto: boolean; instrucoes: string };
const FORM0: FormRec = { nome: "", desc: "", aprov: true, taxa: "", boleto: false, instrucoes: "" };

export function ReservasPage({
  condominioId, onVoltar, gestao = true,
}: {
  condominioId: number;
  onVoltar: () => void;
  gestao?: boolean;   // false = Portal: morador só consulta e SOLICITA; cadastro/aprovação ficam no menu inicial
}) {
  const qc = useQueryClient();
  const inval = () => {
    qc.invalidateQueries({ queryKey: ["recursos", condominioId] });
    qc.invalidateQueries({ queryKey: ["reservas", condominioId] });
    qc.invalidateQueries({ queryKey: ["reservas-cobrancas", condominioId] });
  };
  const { data: rec } = useQuery<RecursosResp>({ queryKey: ["recursos", condominioId], queryFn: () => api.get(`/condominios/${condominioId}/recursos`) });
  const { data: res } = useQuery<ReservasResp>({ queryKey: ["reservas", condominioId], queryFn: () => api.get(`/condominios/${condominioId}/reservas`) });

  // No Portal (gestao=false) o síndico também só consulta/solicita; cadastro de recursos
  // e aprovação de reservas ficam no menu inicial do condomínio.
  const podeGerir = gestao && (rec?.pode_gerir ?? false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  // Recurso novo ou em edição (gestão)
  const [formRec, setFormRec] = useState<FormRec | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const salvarRecurso = useMutation({
    mutationFn: (f: FormRec) => {
      const taxa = f.taxa ? Number(f.taxa.replace(",", ".")) : null;
      const corpo = { nome: f.nome, descricao: f.desc || null, precisa_aprovacao: f.aprov, taxa,
        cobranca_boleto: !!taxa && f.boleto, pagamento_instrucoes: f.boleto ? null : (f.instrucoes || null) };
      return editId ? api.patch(`/recursos/${editId}`, corpo) : api.post(`/condominios/${condominioId}/recursos`, corpo);
    },
    onSuccess: () => { setFormRec(null); setEditId(null); setErro(null); inval(); },
    onError: (e) => setErro(msgErro(e, "Não foi possível salvar o recurso.")),
  });
  const excluirRecurso = useMutation({ mutationFn: (id: number) => api.delete(`/recursos/${id}`), onSuccess: inval });

  // Solicitar reserva (morador)
  const [reservando, setReservando] = useState<Recurso | null>(null);
  const [data, setData] = useState(""); const [hIni, setHIni] = useState("18:00"); const [hFim, setHFim] = useState("22:00");
  const [unidade, setUnidade] = useState("");
  const [aceite, setAceite] = useState(false);
  const minhas = rec?.minhas_unidades ?? [];
  const unidadeEscolhida = minhas.length === 1 ? minhas[0] : unidade;
  const solicitar = useMutation({
    mutationFn: (recurso: Recurso) => api.post(`/condominios/${condominioId}/reservas`, {
      recurso_id: recurso.id, inicio: `${data}T${hIni}:00`, fim: `${data}T${hFim}:00`,
      unidade: unidadeEscolhida || null, aceite_cobranca: aceite,
    }),
    onSuccess: () => { setReservando(null); setData(""); setAceite(false); setErro(null); inval(); },
    onError: (e) => setErro(msgErro(e, "Não foi possível reservar.")),
  });
  const mudarReserva = useMutation({
    mutationFn: (a: { id: number; status: string }) =>
      api.patch<{ avisar_administradora?: boolean }>(`/reservas/${a.id}`, { status: a.status }),
    onSuccess: (r) => {
      setAviso(r?.avisar_administradora
        ? "Esta cobrança já tinha sido enviada à administradora. Ela entrou na lista \"Pedir cancelamento da cobrança\" do síndico."
        : null);
      inval();
    },
  });

  function cancelar(x: Reserva) {
    const cob = x.cobranca_boleto && x.valor ? `\n\nA cobrança de ${brl(x.valor)} no boleto também será cancelada.` : "";
    if (confirm(`Cancelar esta reserva?${cob}`)) mudarReserva.mutate({ id: x.id, status: "cancelada" });
  }

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft size={16} /> Voltar
      </button>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-brand-700 text-white flex items-center justify-center"><CalendarClock size={20} /></div>
          <h1 className="text-2xl font-bold text-slate-900">Reservas</h1>
        </div>
        {podeGerir && !formRec && (
          <button onClick={() => { setEditId(null); setFormRec({ ...FORM0 }); setErro(null); }} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 text-white px-3 py-2 text-sm font-medium hover:bg-brand-800">
            <Plus size={16} /> Novo recurso
          </button>
        )}
      </div>
      {erro && <div className="mb-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{erro}</div>}
      {aviso && <div className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">{aviso}</div>}

      {formRec && (
        <FormRecurso f={formRec} set={setFormRec} editando={!!editId} salvando={salvarRecurso.isPending}
          onSalvar={() => salvarRecurso.mutate(formRec)} onFechar={() => { setFormRec(null); setEditId(null); }} />
      )}

      {podeGerir && <CobrancasBoleto condominioId={condominioId} />}

      {/* Recursos */}
      <h2 className="text-sm font-semibold text-slate-700 mb-2">Recursos disponíveis</h2>
      <div className="space-y-2 mb-6">
        {(rec?.itens ?? []).length === 0 && <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-xl p-6 text-center">Nenhum recurso cadastrado ainda.</div>}
        {(rec?.itens ?? []).map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-slate-900">{r.nome}</div>
                {r.descricao && <div className="text-[12px] text-slate-500">{r.descricao}</div>}
                <div className="text-[11px] text-slate-400 mt-0.5">
                  {r.precisa_aprovacao ? "Precisa aprovação" : "Confirmação automática"}
                  {r.taxa != null && ` · taxa ${brl(r.taxa)} · ${r.cobranca_boleto ? "cobrada no próximo boleto" : "pagamento combinado direto"}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { setReservando(r); setAceite(false); setErro(null); }} className="rounded-lg bg-brand-700 text-white px-3 py-1.5 text-sm font-medium hover:bg-brand-800">Reservar</button>
                {podeGerir && (
                  <button onClick={() => {
                    setEditId(r.id); setErro(null);
                    setFormRec({ nome: r.nome, desc: r.descricao ?? "", aprov: r.precisa_aprovacao,
                      taxa: r.taxa != null ? String(r.taxa).replace(".", ",") : "", boleto: r.cobranca_boleto,
                      instrucoes: r.pagamento_instrucoes ?? "" });
                  }} className="text-slate-400 hover:text-slate-700" title="Editar"><Pencil size={16} /></button>
                )}
                {podeGerir && <button onClick={() => { if (confirm(`Arquivar "${r.nome}"?`)) excluirRecurso.mutate(r.id); }} className="text-slate-400 hover:text-rose-600" title="Arquivar"><Trash2 size={16} /></button>}
              </div>
            </div>
            {reservando?.id === r.id && (
              <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
                <div className="flex flex-wrap items-end gap-2">
                  <label className="text-[12px] text-slate-600">Data<input type="date" value={data} onChange={(e) => setData(e.target.value)} className="block rounded-lg border border-slate-300 px-2 py-1 text-sm" /></label>
                  <label className="text-[12px] text-slate-600">Início<input type="time" value={hIni} onChange={(e) => setHIni(e.target.value)} className="block rounded-lg border border-slate-300 px-2 py-1 text-sm" /></label>
                  <label className="text-[12px] text-slate-600">Fim<input type="time" value={hFim} onChange={(e) => setHFim(e.target.value)} className="block rounded-lg border border-slate-300 px-2 py-1 text-sm" /></label>
                  {minhas.length > 1 && (
                    <label className="text-[12px] text-slate-600">Apartamento
                      <select value={unidade} onChange={(e) => setUnidade(e.target.value)} className="block rounded-lg border border-slate-300 px-2 py-1 text-sm">
                        <option value="">escolha…</option>
                        {minhas.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </label>
                  )}
                </div>
                {r.taxa != null && r.cobranca_boleto && (
                  <label className="flex items-start gap-2 text-[12px] text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                    <input type="checkbox" className="mt-0.5" checked={aceite} onChange={(e) => setAceite(e.target.checked)} />
                    <span><b>Taxa de {brl(r.taxa)} no boleto.</b> {textoAceite(r, data, hIni, unidadeEscolhida || null)}</span>
                  </label>
                )}
                {r.taxa != null && !r.cobranca_boleto && (
                  <div className="text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                    <b>Taxa de {brl(r.taxa)}</b> — {r.pagamento_instrucoes || "o pagamento é combinado diretamente com o síndico."}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button onClick={() => solicitar.mutate(r)}
                    disabled={!data || solicitar.isPending || (minhas.length > 1 && !unidade) || (r.cobranca_boleto && r.taxa != null && !aceite)}
                    className="rounded-lg bg-emerald-600 text-white px-3 py-1.5 text-sm font-medium disabled:opacity-40">
                    {solicitar.isPending ? <Loader2 size={14} className="animate-spin" /> : "Solicitar"}
                  </button>
                  <button onClick={() => setReservando(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Agenda */}
      <h2 className="text-sm font-semibold text-slate-700 mb-2">Reservas</h2>
      <div className="space-y-2">
        {(res?.itens ?? []).length === 0 && <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-xl p-6 text-center">Nenhuma reserva ainda.</div>}
        {(res?.itens ?? []).map((x) => (
          <div key={x.id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-medium text-slate-900">
                {x.recurso_nome} <span className={`ml-1 px-2 py-0.5 rounded-md text-[11px] ${CORES[x.status] ?? "bg-slate-100"}`}>{x.status}</span>
                {x.cobranca_boleto && x.valor != null && (
                  <span className="ml-1 px-2 py-0.5 rounded-md text-[11px] bg-violet-50 text-violet-700 border border-violet-200" title={x.aceite_texto ?? ""}>
                    {brl(x.valor)} no boleto{x.informada_adm_em ? " · enviada à adm." : ""}
                  </span>
                )}
              </div>
              <div className="text-[12px] text-slate-500">{fmtDT(x.inicio)} – {fmtDT(x.fim)}{x.unidade ? ` · ${x.unidade}` : ""}{x.usuario_nome ? ` · ${x.usuario_nome}` : ""}</div>
            </div>
            <div className="flex items-center gap-1.5">
              {gestao && res?.pode_gerir && x.status === "solicitada" && (
                <>
                  <button onClick={() => mudarReserva.mutate({ id: x.id, status: "aprovada" })} title="Aprovar" className="rounded-md bg-emerald-600 text-white p-1.5"><Check size={15} /></button>
                  <button onClick={() => mudarReserva.mutate({ id: x.id, status: "recusada" })} title="Recusar" className="rounded-md bg-white border border-rose-300 text-rose-700 p-1.5"><X size={15} /></button>
                </>
              )}
              {(x.meu || (gestao && res?.pode_gerir)) && (x.status === "solicitada" || x.status === "aprovada") && (
                <button onClick={() => cancelar(x)} className="text-[12px] text-slate-500 hover:text-rose-600 px-2">Cancelar</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FormRecurso({ f, set, editando, salvando, onSalvar, onFechar }: {
  f: FormRec; set: (f: FormRec) => void; editando: boolean; salvando: boolean; onSalvar: () => void; onFechar: () => void;
}) {
  const temTaxa = !!f.taxa && Number(f.taxa.replace(",", ".")) > 0;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 space-y-2">
      <div className="text-sm font-semibold text-slate-800">{editando ? "Editar recurso" : "Novo recurso"}</div>
      <input placeholder="Nome do recurso (ex.: Salão de festas)" value={f.nome} onChange={(e) => set({ ...f, nome: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      <input placeholder="Descrição / regras (opcional)" value={f.desc} onChange={(e) => set({ ...f, desc: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5 text-sm text-slate-700"><input type="checkbox" checked={f.aprov} onChange={(e) => set({ ...f, aprov: e.target.checked })} /> Precisa aprovação do síndico</label>
        <label className="flex items-center gap-1.5 text-sm text-slate-700">Taxa (R$): <input value={f.taxa} onChange={(e) => set({ ...f, taxa: e.target.value })} className="w-24 rounded-lg border border-slate-300 px-2 py-1 text-sm" /></label>
      </div>
      {temTaxa && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 space-y-1.5">
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" className="mt-1" checked={f.boleto} onChange={(e) => set({ ...f, boleto: e.target.checked })} />
            <span>Cobrar a taxa no <b>próximo boleto</b> (pela administradora)
              <span className="block text-[11px] text-slate-500">O morador só reserva depois de aceitar a cobrança. Você manda a lista à administradora em "Cobranças no boleto".</span></span>
          </label>
          {!f.boleto && (
            <input placeholder="Como pagar (ex.: pague a limpeza direto à diarista)" value={f.instrucoes}
              onChange={(e) => set({ ...f, instrucoes: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          )}
        </div>
      )}
      {editando && <p className="text-[11px] text-slate-400">Mudanças valem para as próximas reservas; as já feitas mantêm o valor que o morador aceitou.</p>}
      <div className="flex justify-end gap-2">
        <button onClick={onFechar} className="px-3 py-2 text-sm text-slate-600">Cancelar</button>
        <button onClick={onSalvar} disabled={!f.nome || salvando} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">Salvar</button>
      </div>
    </div>
  );
}

/**
 * Gestão: cobranças a mandar à administradora (PDF + e-mail pronto) e pedidos de cancelamento.
 *
 * Ao abrir o quadro, a tela "congela" o ENVIO (as reservas a mandar agora). Baixar o PDF,
 * copiar o texto ou abrir o e-mail REGISTRA o envio sozinho — antes dependia de um botão
 * "Marcar como enviadas", fácil de esquecer (aí um cancelamento posterior não virava pedido
 * de cancelamento da cobrança). O PDF e o e-mail usam a lista congelada, então continuam
 * iguais depois de registrados. Desde o teste na homologação (19/10), os botões de e-mail
 * baixam o PDF antes de abrir o e-mail — e baixar o PDF já registra o envio no servidor.
 */
function CobrancasBoleto({ condominioId }: { condominioId: number }) {
  const qc = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [lote, setLote] = useState<ItemCobranca[] | null>(null);
  const { data } = useQuery<Cobrancas>({
    queryKey: ["reservas-cobrancas", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/reservas/cobrancas`),
  });
  const ids = (lote ?? []).map((i) => i.id).join(",");
  const { data: texto } = useQuery<{ assunto: string; corpo: string; administradora: string | null }>({
    queryKey: ["reservas-cobrancas-texto", condominioId, ids],
    queryFn: () => api.get(`/condominios/${condominioId}/reservas/cobrancas/texto-administradora?ids=${ids}`),
    enabled: aberto && !!ids,
  });
  const atualizar = () => {
    qc.invalidateQueries({ queryKey: ["reservas-cobrancas", condominioId] });
    qc.invalidateQueries({ queryKey: ["reservas", condominioId] });
  };
  if (!data) return null;
  const pend = data.pendentes, est = data.estornar;
  const total = pend.reduce((s, i) => s + i.valor, 0);
  const nada = !pend.length && !est.length && !data.aguardando_aprovacao.length && !data.informadas.length
    && !data.estornos_pedidos.length;
  if (nada) return null;

  function abrirFechar() {
    if (!aberto) setLote([...pend, ...est]);      // congela o envio
    else setLote(null);
    setAberto(!aberto);
  }
  const linha = (i: ItemCobranca) => `${i.unidade ?? "?"} · ${i.recurso_nome} · ${fmtDT(i.inicio)} · ${brl(i.valor)}`;
  const lotePend = (lote ?? []).filter((i) => i.status === "aprovada");
  const loteEst = (lote ?? []).filter((i) => i.status !== "aprovada");

  return (
    <div className="bg-white border border-violet-200 rounded-xl p-4 mb-5">
      <button onClick={abrirFechar} className="w-full flex items-center justify-between gap-2 text-left">
        <span className="font-semibold text-slate-800 flex items-center gap-2"><Receipt size={16} className="text-violet-600" /> Cobranças no boleto</span>
        <span className="text-[12px] text-slate-600">
          {pend.length > 0 && <span className="px-2 py-0.5 rounded-md bg-violet-100 text-violet-800 mr-1">{pend.length} a enviar · {brl(total)}</span>}
          {est.length > 0 && <span className="px-2 py-0.5 rounded-md bg-rose-100 text-rose-700">{est.length} cancelamento(s) a pedir</span>}
          {!pend.length && !est.length && "nada pendente"}
        </span>
      </button>
      {aberto && (
        <div className="mt-3 space-y-3 text-[12px] text-slate-700">
          {lotePend.length > 0 && (
            <div>
              <div className="font-medium mb-1">Cobrar no próximo boleto (aprovadas, com o aceite do morador):</div>
              <ul className="list-disc pl-5 space-y-0.5">{lotePend.map((i) => <li key={i.id}>{linha(i)}</li>)}</ul>
            </div>
          )}
          {loteEst.length > 0 && (
            <div className="text-rose-700">
              <div className="font-medium mb-1">Pedir cancelamento da cobrança (canceladas ou recusadas depois de enviadas):</div>
              <ul className="list-disc pl-5 space-y-0.5">{loteEst.map((i) => <li key={i.id}>{linha(i)}</li>)}</ul>
            </div>
          )}
          {data.aguardando_aprovacao.length > 0 && (
            <p className="text-slate-500">{data.aguardando_aprovacao.length} reserva(s) com taxa aguardando a sua aprovação — só entram na cobrança depois de aprovadas.</p>
          )}
          {(lote ?? []).length > 0 ? (
            <>
              <ol className="list-decimal pl-5 space-y-1.5">
                <li>
                  <a href={reservasCobrancasPdfUrl(condominioId, ids)} download
                    onClick={() => setTimeout(atualizar, 1500)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-brand-700 text-white"><Download size={13} /> Baixar o PDF para a administradora</a>
                </li>
                <li>
                  <BotoesEmail assunto={texto?.assunto} corpo={texto?.corpo} pdfUrl={reservasCobrancasPdfUrl(condominioId, ids)}
                    onUsar={() => setTimeout(atualizar, 1500)} />
                </li>
                <li>Anexe o PDF baixado (clipe do e-mail) e envie à administradora.</li>
              </ol>
              <p className="text-[11px] text-amber-700">{AVISO_ANEXO}</p>
              <p className="text-[11px] text-slate-500 flex items-center gap-1"><Briefcase size={12} />
                Ao baixar o PDF, copiar o texto ou abrir o e-mail, o app já registra este envio. Se depois uma dessas reservas
                for cancelada, ela aparece aqui para você pedir o cancelamento da cobrança.</p>
            </>
          ) : (
            <p className="text-slate-500">Nada a enviar agora.</p>
          )}
          {texto && (lote ?? []).length > 0 && (
            <pre className="whitespace-pre-wrap font-sans text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 max-h-48 overflow-auto">{`${texto.assunto}\n\n${texto.corpo}`}</pre>
          )}
          {(data.informadas.length > 0 || data.estornos_pedidos.length > 0) && (
            <details>
              <summary className="cursor-pointer text-slate-500">Histórico de envios ({data.informadas.length + data.estornos_pedidos.length})</summary>
              <ul className="list-disc pl-5 mt-1 space-y-0.5 text-slate-500">
                {data.informadas.map((i) => <li key={`c${i.id}`}>{linha(i)} · cobrança enviada {fmtDT(i.informada_adm_em)}</li>)}
                {data.estornos_pedidos.map((i) => <li key={`e${i.id}`}>{linha(i)} · cancelamento pedido {fmtDT(i.estorno_adm_em)}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
